'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from './ui/AppLogo';
import { LayoutDashboard, Users, FileText, TrendingUp, ShieldAlert, Package, MapPin, Settings, ChevronLeft, ChevronRight, Bell, LogOut, Building2, Banknote, BarChart3, UserCircle, ShieldCheck, RefreshCw, Globe, Plane, Map, PieChart, Target, AlertTriangle, Download, Presentation, CreditCard, Calculator, ClipboardList, DatabaseZap, Receipt, FlaskConical, Activity, X, Layers, Landmark, Smartphone, ClipboardCheck, Search, ScrollText, ArrowLeftRight, FolderOpen, Scale, Cpu } from 'lucide-react';
import { useAuth, UserRole, AuthorityWorkspace } from '@/context/AuthContext';

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

interface NavItem {
  href: string;
  label: string;
  icon: React.ElementType;
  badge: string | null;
  description: string;
  roles?: UserRole[];
  workspace?: AuthorityWorkspace | 'both';
}

interface NavGroup {
  label: string;
  roles?: UserRole[];
  workspace?: AuthorityWorkspace | 'both';
  items: NavItem[];
}

// ── LRA WORKSPACE NAV ──────────────────────────────────────────────────────
const lraNavGroups: NavGroup[] = [
  {
    label: 'Dashboard',
    workspace: 'lra',
    items: [
      { href: '/', label: 'Revenue Command', icon: LayoutDashboard, badge: null, description: 'Executive Intelligence Dashboard', roles: ['executive', 'administrator', 'lra_staff'] },
      { href: '/taxpayer-self-service-portal', label: 'Taxpayer Portal', icon: UserCircle, badge: null, description: 'Citizen Self-Service', roles: ['executive', 'administrator', 'lra_staff', 'taxpayer'] },
    ],
  },
  {
    label: 'Administration',
    workspace: 'lra',
    items: [
      { href: '/taxpayer-registry', label: 'Taxpayer Registry', icon: Users, badge: '3', description: 'Taxpayer Identity & Profiles', roles: ['executive', 'administrator', 'lra_staff'] },
      { href: '/compliance-engine', label: 'Compliance Engine', icon: ShieldAlert, badge: '12', description: 'AI Compliance Monitoring', roles: ['executive', 'administrator', 'lra_staff'] },
      { href: '/tax-filings', label: 'Tax Filings', icon: FileText, badge: null, description: 'Returns & Assessments', roles: ['executive', 'administrator', 'lra_staff'] },
      { href: '/collections', label: 'Collections Dashboard', icon: Banknote, badge: 'S7', description: 'Stage 7 · Payments, Reconciliation & Treasury', roles: ['executive', 'administrator', 'lra_staff'] },
      { href: '/property-payments-matching', label: 'Property Payments', icon: CreditCard, badge: 'S8', description: 'Stage 8 · Payment Matching & Velocity', roles: ['executive', 'administrator', 'lra_staff'] },
      { href: '/valuation-config', label: 'Valuation Config', icon: Calculator, badge: 'S4', description: 'Stage 4 · Valuation Formulas & Rules', roles: ['executive', 'administrator'] },
      { href: '/tax-assessment-admin', label: 'Tax Assessment', icon: ClipboardList, badge: 'S5', description: 'Stage 5 · Auto-Calculate & Approve Taxes', roles: ['executive', 'administrator'] },
      { href: '/billing-dashboard', label: 'Billing Dashboard', icon: Receipt, badge: 'S6', description: 'Stage 6 · Generate & Dispatch Tax Bills', roles: ['executive', 'administrator', 'lra_staff'] },
      { href: '/property-registration-admin', label: 'Property Registration', icon: DatabaseZap, badge: 'S1', description: 'Stage 1 · Capture, Geolocate & Register', roles: ['executive', 'administrator', 'lra_staff'] },
      { href: '/parcel-delineation', label: 'Parcel Delineation', icon: Map, badge: 'S2', description: 'Stage 2 · Draw Boundaries & Assign Parcel IDs', roles: ['executive', 'administrator', 'lra_staff'] },
      { href: '/ownership-verification', label: 'Ownership Verification', icon: ShieldCheck, badge: 'S3', description: 'Stage 3 · Verify Ownership & Approve for Valuation', roles: ['executive', 'administrator', 'lra_staff'] },
      { href: '/valuation-admin', label: 'Valuation Admin', icon: FlaskConical, badge: 'S4', description: 'Stage 4 · Formulas, Parcel Queue & Forward to S5', roles: ['executive', 'administrator'] },
    ],
  },
  {
    label: 'Analytics & Intelligence',
    workspace: 'lra',
    items: [
      { href: '/operations-overview', label: 'Operations Overview', icon: Activity, badge: null, description: 'Officer Activity · Pipeline · Overrides', roles: ['executive', 'administrator', 'lra_staff'] },
      { href: '/revenue-analytics', label: 'Revenue Analytics', icon: BarChart3, badge: null, description: 'Forecasting & Trends', roles: ['executive', 'administrator'] },
      { href: '/county-payment-performance', label: 'County Performance', icon: PieChart, badge: null, description: 'Payment Performance by County', roles: ['executive', 'administrator'] },
      { href: '/customs-border', label: 'Customs & Border', icon: Package, badge: '5', description: 'Cargo Risk & Trade Intel', roles: ['executive', 'administrator'] },
      { href: '#', label: 'Audit Cases', icon: TrendingUp, badge: '8', description: 'Enforcement & Audits', roles: ['executive', 'administrator'] },
    ],
  },
  {
    label: 'GIS & Mapping',
    workspace: 'lra',
    items: [
      { href: '/gis-property-hub', label: 'GIS Property Hub', icon: MapPin, badge: null, description: 'GIS Property Intelligence', roles: ['executive', 'administrator', 'lra_staff'] },
      { href: '/gis-property-hub?tab=drone', label: 'Drone Mapping', icon: Plane, badge: 'NEW', description: 'Aerial Survey & Fleet Status', roles: ['executive', 'administrator', 'lra_staff'] },
      { href: '/auto-valuation', label: 'Auto-Valuation Engine', icon: Cpu, badge: null, description: 'CAMA Mass Appraisal · Class & Zone', roles: ['executive', 'administrator', 'lra_staff'] },
    ],
  },
  {
    label: 'Methodology',
    workspace: 'lra',
    items: [
      { href: '/property-tax-cycle', label: 'Tax Cycle', icon: RefreshCw, badge: '9', description: '9-Stage Property Tax Cycle', roles: ['executive', 'administrator', 'lra_staff'] },
      { href: '/system-methodology', label: 'System Methodology', icon: Globe, badge: null, description: 'WB GIS Framework Alignment', roles: ['executive', 'administrator', 'lra_staff'] },
      { href: '/nine-circle-methodology', label: '9-Circle Deep Dive', icon: Target, badge: 'NEW', description: 'How LRA Nexus Handles Each Stage', roles: ['executive', 'administrator', 'lra_staff'] },
    ],
  },
  {
    label: 'Presentation',
    workspace: 'lra',
    items: [
      { href: '/executive-briefing', label: 'Executive Briefing', icon: Presentation, badge: 'NEW', description: 'Problem → Solution → ROI → Action', roles: ['executive', 'administrator'] },
      { href: '/problem-solution', label: 'Problem & Solution', icon: AlertTriangle, badge: 'KEY', description: 'Cost of Inaction · Revenue Gap · IMF Benchmarks', roles: ['executive', 'administrator'] },
      { href: '/resources', label: 'Resources & Downloads', icon: Download, badge: null, description: 'WB GIS · LRA 9-Circle · IMF Docs', roles: ['executive', 'administrator', 'lra_staff'] },
    ],
  },
  {
    label: 'System',
    workspace: 'lra',
    items: [
      { href: '/enterprise-admin', label: 'Enterprise Admin', icon: ShieldCheck, badge: null, description: 'RBAC · Tax Config · Permissions', roles: ['executive', 'administrator'] },
      { href: '/county-offices', label: 'County Offices', icon: Building2, badge: null, description: 'Field Office Management', roles: ['executive', 'administrator', 'lra_staff'] },
      { href: '#', label: 'Settings', icon: Settings, badge: null, description: 'Platform Configuration', roles: ['executive', 'administrator'] },
    ],
  },
];

// ── LLA WORKSPACE NAV ──────────────────────────────────────────────────────
const llaNavGroups: NavGroup[] = [
  {
    label: 'Cadastral Command',
    workspace: 'lla',
    items: [
      { href: '/lla/parcel-registry', label: 'Parcel Master Registry', icon: Layers, badge: null, description: 'National Land Record & Deed Vault', roles: ['executive', 'administrator', 'lla_staff', 'county_admin'] },
      { href: '/lla/cadastral-map', label: 'GIS Cadastral Map', icon: Map, badge: null, description: '7 Counties · 9 Cities · Parcel Overlays', roles: ['executive', 'administrator', 'lla_staff', 'lra_staff', 'county_admin', 'field_enumerator'] },
    ],
  },
  {
    label: 'Land Records',
    workspace: 'lla',
    items: [
      { href: '/lla/ownership-tenure', label: 'Ownership & Tenure', icon: Landmark, badge: null, description: 'Title Chain · Transfers · Encumbrances', roles: ['executive', 'administrator', 'lla_staff'] },
      { href: '/lla/ownership-tenure?tab=deeds', label: 'Deed Vault', icon: FolderOpen, badge: null, description: 'Legal Deeds · Survey Certs · Court Orders', roles: ['executive', 'administrator', 'lla_staff'] },
      { href: '/lla/objections-appeals', label: 'Objections & Appeals', icon: Scale, badge: '4', description: 'Valuation Disputes · Hearing Schedule', roles: ['executive', 'administrator', 'lla_staff', 'lra_staff'] },
    ],
  },
  {
    label: 'Field Operations',
    workspace: 'lla',
    items: [
      { href: '/lla/field-enumerator', label: 'Field Enumerator', icon: Smartphone, badge: null, description: 'Offline-First Parcel Capture', roles: ['executive', 'administrator', 'lla_staff', 'field_enumerator', 'county_admin'] },
      { href: '/lla/county-qa', label: 'County QA Pipeline', icon: ClipboardCheck, badge: '7', description: 'Submission Review · Approve · Escalate', roles: ['executive', 'administrator', 'lla_staff', 'county_admin'] },
    ],
  },
  {
    label: 'Public & Audit',
    workspace: 'lla',
    items: [
      { href: '/lla/public-lookup', label: 'Public Parcel Lookup', icon: Search, badge: null, description: 'Citizen Parcel Search — No Login Required', roles: ['executive', 'administrator', 'lla_staff', 'lra_staff', 'taxpayer', 'field_enumerator', 'county_admin'] },
      { href: '/lla/national-audit', label: 'National Audit Console', icon: ScrollText, badge: null, description: 'Full Write-Action Log · Both Authorities', roles: ['executive', 'administrator'] },
    ],
  },
  {
    label: 'System',
    workspace: 'lla',
    items: [
      { href: '/enterprise-admin', label: 'Enterprise Admin', icon: ShieldCheck, badge: null, description: 'RBAC · Permissions · Config', roles: ['executive', 'administrator'] },
      { href: '/county-offices', label: 'County Offices', icon: Building2, badge: null, description: 'Field Office Management', roles: ['executive', 'administrator', 'lla_staff', 'county_admin'] },
    ],
  },
];

const ROLE_LABELS: Record<UserRole, string> = {
  executive: 'Executive',
  administrator: 'Administrator',
  lra_staff: 'LRA Staff',
  taxpayer: 'Taxpayer',
  lla_staff: 'LLA Staff',
  field_enumerator: 'Field Enumerator',
  county_admin: 'County Admin',
};

const ROLE_COLORS: Record<UserRole, string> = {
  executive: '#d4a017',
  administrator: '#60a5fa',
  lra_staff: '#34d399',
  taxpayer: '#a78bfa',
  lla_staff: '#f97316',
  field_enumerator: '#22d3ee',
  county_admin: '#fb7185',
};

const WORKSPACE_CONFIG = {
  lra: { label: 'LRA', fullLabel: 'Revenue Authority', color: '#34d399', bg: 'rgba(52,211,153,0.12)', border: 'rgba(52,211,153,0.25)' },
  lla: { label: 'LLA', fullLabel: 'Land Authority', color: '#f97316', bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.25)' },
};

export default function Sidebar({ collapsed, onToggleCollapse, mobileOpen = false, onMobileClose }: SidebarProps) {
  const pathname = usePathname();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const { user, logout, activeWorkspace, setActiveWorkspace } = useAuth();

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    const basePath = href.split('?')[0];
    return pathname.startsWith(basePath);
  };

  const canSeeItem = (item: NavItem) => {
    if (!item.roles) return true;
    if (!user) return true;
    return item.roles.includes(user.role);
  };

  const canSeeGroup = (group: NavGroup) => {
    return group.items.some(canSeeItem);
  };

  const userRole = user?.role ?? 'executive';
  const roleColor = ROLE_COLORS[userRole];
  const roleLabel = ROLE_LABELS[userRole];
  const userInitials = user?.initials ?? 'JJ';
  const userName = user?.name ?? 'J. Jallah';
  const userTitle = user?.title ?? 'Commissioner General';

  // LRA sidebar always shows LRA nav — LLA workspace has its own dedicated sidebar
  const currentGroups = lraNavGroups;
  const wsConfig = WORKSPACE_CONFIG['lra'];

  const sidebarContent = (isMobile: boolean) => (
    <>
      {/* Logo + Workspace Badge */}
      <div className="flex items-center h-16 px-3 border-b flex-shrink-0" style={{ borderColor: 'var(--dark-border)' }}>
        <div className="flex items-center gap-2 overflow-hidden flex-1">
          <AppLogo size={32} />
          {(!collapsed || isMobile) && (
            <div className="flex flex-col overflow-hidden">
              <span className="font-bold text-sm leading-tight whitespace-nowrap" style={{ color: 'var(--dark-text)' }}>
                LRA Nexus
              </span>
              <span className="text-xs whitespace-nowrap" style={{ color: wsConfig.color }}>
                Revenue Authority
              </span>
            </div>
          )}
        </div>
        {isMobile && (
          <button onClick={onMobileClose} className="p-1.5 rounded-md hover:bg-white/10 transition-colors ml-auto" aria-label="Close menu">
            <X size={18} style={{ color: 'var(--dark-muted)' }} />
          </button>
        )}
      </div>

      {/* Authority Workspace Switcher — always visible for all users */}
      <div className={`mx-2 mt-2 ${(!collapsed || isMobile) ? 'p-1 rounded-xl flex gap-1' : 'flex flex-col gap-1'}`} style={{ backgroundColor: (!collapsed || isMobile) ? 'rgba(255,255,255,0.04)' : 'transparent', border: (!collapsed || isMobile) ? '1px solid var(--dark-border)' : 'none' }}>
        {/* LRA tab — active (current workspace) */}
        <button
          className={`flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 relative group ${(!collapsed || isMobile) ? 'flex-1' : 'w-full'}`}
          style={{
            backgroundColor: WORKSPACE_CONFIG.lra.bg,
            color: WORKSPACE_CONFIG.lra.color,
            border: `1px solid ${WORKSPACE_CONFIG.lra.border}`,
          }}
          title={collapsed && !isMobile ? 'LRA — Revenue Authority (Active)' : undefined}
        >
          <ArrowLeftRight size={11} />
          {(!collapsed || isMobile) && 'LRA'}
          {collapsed && !isMobile && (
            <div className="absolute left-full ml-2 z-50 px-2 py-1 rounded text-xs whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150" style={{ backgroundColor: 'var(--dark-elevated)', color: WORKSPACE_CONFIG.lra.color, border: `1px solid ${WORKSPACE_CONFIG.lra.border}` }}>
              LRA — Revenue Authority (Active)
            </div>
          )}
        </button>
        {/* LLA tab — navigates to LLA workspace */}
        <button
          onClick={() => { setActiveWorkspace('lla'); window.location.href = '/lla/parcel-registry'; }}
          className={`flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 hover:bg-white/10 relative group ${(!collapsed || isMobile) ? 'flex-1' : 'w-full'}`}
          style={{
            backgroundColor: 'transparent',
            color: 'var(--dark-muted)',
            border: '1px solid var(--dark-border)',
          }}
          title={collapsed && !isMobile ? 'Switch to LLA — Land Authority' : undefined}
        >
          <ArrowLeftRight size={11} />
          {(!collapsed || isMobile) && 'LLA'}
          {collapsed && !isMobile && (
            <div className="absolute left-full ml-2 z-50 px-2 py-1 rounded text-xs whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150" style={{ backgroundColor: 'var(--dark-elevated)', color: WORKSPACE_CONFIG.lla.color, border: `1px solid ${WORKSPACE_CONFIG.lla.border}` }}>
              Switch to LLA — Land Authority
            </div>
          )}
        </button>
      </div>

      {/* Role badge */}
      {(!collapsed || isMobile) && user && (
        <div className="mx-2 mt-1.5 px-2 py-1.5 rounded-lg flex items-center gap-2" style={{ backgroundColor: `${roleColor}15`, border: `1px solid ${roleColor}30` }}>
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: roleColor }} />
          <span className="text-xs font-semibold truncate" style={{ color: roleColor }}>{roleLabel}</span>
          {user.jurisdiction && (
            <span className="text-xs truncate ml-auto" style={{ color: 'var(--dark-muted)' }}>{user.jurisdiction}</span>
          )}
        </div>
      )}

      {/* Nav Groups */}
      <nav className="flex-1 py-3 px-2 overflow-y-auto scrollbar-thin">
        {currentGroups.filter(canSeeGroup).map((group, gi) => (
          <div key={`group-${gi}`} className="mb-4">
            {(!collapsed || isMobile) && (
              <p className="text-xs font-semibold uppercase tracking-widest px-2 mb-1" style={{ color: 'var(--dark-muted)', letterSpacing: '0.1em' }}>
                {group.label}
              </p>
            )}
            {collapsed && !isMobile && gi > 0 && (
              <div className="mx-2 mb-2 border-t" style={{ borderColor: 'var(--dark-border)' }} />
            )}
            {group.items.filter(canSeeItem).map((item) => {
              const ItemIcon = item.icon;
              const active = isActive(item.href);
              const isNew = item.badge === 'NEW';
              return (
                <Link
                  key={`nav-${item.href}-${item.label}`}
                  href={item.href}
                  prefetch={!item.href.startsWith('#')}
                  onMouseEnter={() => setHoveredItem(item.label)}
                  onMouseLeave={() => setHoveredItem(null)}
                  onClick={isMobile ? onMobileClose : undefined}
                  className="relative flex items-center gap-3 px-2 py-2 rounded-md mb-0.5 transition-all duration-150 group"
                  style={{
                    backgroundColor: active ? `${wsConfig.color}20` : hoveredItem === item.label ? 'rgba(255,255,255,0.05)' : 'transparent',
                    color: active ? wsConfig.color : 'var(--dark-text)',
                  }}
                  title={collapsed && !isMobile ? item.label : undefined}
                >
                  <ItemIcon size={18} className="flex-shrink-0" style={{ color: active ? wsConfig.color : 'var(--dark-muted)' }} />
                  {(!collapsed || isMobile) && (
                    <span className="text-sm font-medium truncate flex-1">{item.label}</span>
                  )}
                  {(!collapsed || isMobile) && item.badge && (
                    <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: isNew ? 'rgba(34,197,94,0.2)' : `${wsConfig.color}20`, color: isNew ? '#22c55e' : wsConfig.color }}>
                      {item.badge}
                    </span>
                  )}
                  {collapsed && !isMobile && (
                    <div className="absolute left-full ml-2 z-50 px-2 py-1 rounded text-xs whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150" style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }}>
                      {item.label}
                      {item.badge && <span className="ml-1 font-semibold" style={{ color: isNew ? '#22c55e' : wsConfig.color }}>({item.badge})</span>}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Bottom: Alerts + User + Collapse */}
      <div className="flex-shrink-0 border-t p-2 space-y-1" style={{ borderColor: 'var(--dark-border)' }}>
        <button className="w-full flex items-center gap-3 px-2 py-2 rounded-md transition-all duration-150 hover:bg-white/5 group relative" style={{ color: 'var(--dark-muted)' }}>
          <div className="relative flex-shrink-0">
            <Bell size={18} />
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full text-xs flex items-center justify-center" style={{ backgroundColor: wsConfig.color }} />
          </div>
          {(!collapsed || isMobile) && <span className="text-sm font-medium">Alerts</span>}
          {collapsed && !isMobile && (
            <div className="absolute left-full ml-2 z-50 px-2 py-1 rounded text-xs whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }}>
              Alerts
            </div>
          )}
        </button>

        <div className="flex items-center gap-2 px-2 py-2 rounded-md" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ backgroundColor: roleColor, color: '#070e1f' }}>
            {userInitials}
          </div>
          {(!collapsed || isMobile) && (
            <div className="flex-1 overflow-hidden">
              <p className="text-xs font-semibold truncate" style={{ color: 'var(--dark-text)' }}>{userName}</p>
              <p className="text-xs truncate" style={{ color: 'var(--dark-muted)' }}>{userTitle}</p>
            </div>
          )}
          {(!collapsed || isMobile) && (
            <button onClick={logout} title="Sign out">
              <LogOut size={14} style={{ color: 'var(--dark-muted)' }} />
            </button>
          )}
        </div>

        {!isMobile && (
          <button onClick={onToggleCollapse} className="w-full flex items-center justify-center py-1.5 rounded-md transition-all duration-150 hover:bg-white/5" style={{ color: 'var(--dark-muted)' }} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        )}
      </div>
    </>
  );

  return (
    <>
      <aside className="relative hidden md:flex flex-col h-screen border-r border-dark transition-all duration-300 ease-in-out flex-shrink-0 scrollbar-thin" style={{ width: collapsed ? '64px' : '240px', backgroundColor: 'var(--dark-surface)', borderColor: 'var(--dark-border)', overflowY: 'auto', overflowX: 'hidden' }}>
        {sidebarContent(false)}
      </aside>
      <aside className={`fixed top-0 left-0 z-50 flex flex-col h-screen w-64 md:hidden transition-transform duration-300 ease-in-out ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`} style={{ backgroundColor: 'var(--dark-surface)', borderRight: '1px solid var(--dark-border)', overflowY: 'auto', overflowX: 'hidden' }}>
        {sidebarContent(true)}
      </aside>
    </>
  );
}